import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { DropdownInfoConfig, DropdownOptionLike } from '../../../core/constants/api-endpoints';
import { APP_DEVELOPED_BY, PaymentMethodEnum } from '../../../core/constants/app-constants';
import { FlatRenterMappingService, PaymentService, TokenStorageService } from '../../../core/services';
import { CommonDropdownComponent } from '../../../core/ui/common-dropdown/common-dropdown.component';
import { LanguageService } from '../../../core/services/language.service';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { downloadPaymentSlipPdf, PaymentReceiptData, printPaymentSlipPdf } from '../payment-receipt.util';

export interface PaymentSaveEvent {
  mode: 'create' | 'edit';
}

export interface PaymentCreatePrefillContext {
  pendingMappingId: number | null;
  houseId: number | null;
  flatId: number | null;
  renterId: number | null;
  paymentMonth: number | null;
  paymentYear: number | null;
}

@Component({
  selector: 'app-make-payment',
  standalone: true,
  imports: [CommonModule, FormsModule, CommonDropdownComponent, MatDatepickerModule, MatInputModule, MatNativeDateModule, MatSnackBarModule, TranslatePipe],
  templateUrl: './make-payment.component.html',
  styleUrls: ['./make-payment.component.css', './../../../../CSSFiles/BTN_Style.css']
})
export class MakePaymentComponent implements OnInit {
  readonly PaymentMethodEnum = PaymentMethodEnum;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialPayment: Record<string, any> | null = null;
  @Input() prefillContext: PaymentCreatePrefillContext | null = null;
  @Input() receiptOrganizationName = '';
  @Input() receiptBrandName = 'HTALLY';
  @Input() receiptBrandLogoUrl: string | null = null;
  @Input() closeAfterSuccessfulAction = false;

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<PaymentSaveEvent>();

  houseDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'houseId',
    apiInfo: 'HOUSE_LIST',
    status: 1,
    valueKey: 'id',
    display1: 'houseName',
    display2: 'houseNo',
    label: 'filter.house',
    placeholder: 'placeholder.selectHouse',
    isSearchable: true
  };

  flatDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'flatId',
    apiInfo: 'FLAT_LIST',
    status: 1,
    valueKey: 'id',
    display1: 'flatName',
    display2: 'floorName',
    label: 'filter.flat',
    placeholder: 'placeholder.selectFlat',
    isSearchable: true
  };

  billingMonthDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'billingMonth',
    valueKey: 'value',
    display1: 'label',
    label: 'payment.billingMonth',
    placeholder: 'placeholder.selectMonth',
    options: [],
    isSearchable: true,
    searchReadonly: true
  };

  payment = {
    id: null as number | null,
    houseId: null as number | null,
    flatId: null as number | null,
    flatRenterMappingId: null as number | null,
    paymentDate: '',
    paymentMonth: null as number | null,
    paymentYear: `${new Date().getFullYear()}`,
    rentAmount: 0,
    gasBillAmount: 0,
    electricBillAmount: 0,
    waterBillAmount: 0,
    serviceChargeAndOtherAmount: 0,
    negativeChargeAmount: 0,
    advanceAdjustedAmount: 0,
    negativeChargeName: '',
    paymentMethod: PaymentMethodEnum.CASH,
    remarks: '',
    status: 1
  };

  flatDropdownFilters: Record<string, unknown> = {};
  pendingMappings: DropdownOptionLike[] = [];
  availableBillingMonths: Array<{ value: string; label: string; mappingId: number }> = [];
  currentAdvanceAmount = 0;
  availableAdvanceAmount = 0;
  selectedRenterName = '';
  showRenterInfo = false;
  selectedRenterInfo: {
    mobile: string;
    email: string;
    photoIdNo: string;
    permanentAddress: string;
    previousAddress: string;
  } = {
    mobile: '',
    email: '',
    photoIdNo: '',
    permanentAddress: '',
    previousAddress: ''
  };

  matDateValue: Date | null = null;
  isSubmitting = false;
  isLoadingPending = false;
  error: string | null = null;
  successMessage: string | null = null;
  showBillFields = false;
  billRequired = {
    gas: false,
    electric: false,
    water: false,
    service: false,
    other: false
  };
  isPrefillLocked = false;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly language: LanguageService,
    private readonly payments: PaymentService,
    private readonly mappings: FlatRenterMappingService,
    private readonly tokens: TokenStorageService
  ) {}

  ngOnInit(): void {
    this.setDefaults();
    this.applyCreatePrefillValues();
    this.applyEditValues();
  }

  closeModal(): void {
    this.close.emit();
  }

  onHouseChange(event: { value: unknown }): void {
    if (this.isPrefillLocked) {
      return;
    }
    this.successMessage = null;
    const houseId = Number(event.value);
    this.payment.houseId = Number.isFinite(houseId) ? houseId : null;
    this.payment.flatId = null;
    this.payment.flatRenterMappingId = null;
    this.flatDropdownFilters = this.payment.houseId ? { 'house.id': this.payment.houseId } : {};
    this.resetPendingState();
  }

  onFlatChange(event: { value: unknown }): void {
    if (this.isPrefillLocked) {
      return;
    }
    this.successMessage = null;
    const flatId = Number(event.value);
    this.payment.flatId = Number.isFinite(flatId) ? flatId : null;
    this.payment.flatRenterMappingId = null;
    this.resetPendingState();
    this.loadPendingByFlat();
  }

  toggleRenterInfo(): void {
    this.showRenterInfo = !this.showRenterInfo;
  }

  onMonthChange(event: { value: unknown }): void {
    if (this.isBillingMonthLocked()) {
      return;
    }
    this.successMessage = null;
    const value = `${event.value ?? ''}`;
    if (!value.includes('-')) {
      this.payment.paymentMonth = null;
      return;
    }

    const [yearText, monthText] = value.split('-');
    const month = Number(monthText);
    const year = Number(yearText);
    this.payment.paymentMonth = Number.isFinite(month) ? month : null;
    this.payment.paymentYear = Number.isFinite(year) ? `${year}` : this.payment.paymentYear;

    const mappingId = this.availableBillingMonths.find((item) => item.value === value)?.mappingId;
    if (mappingId) {
      this.payment.flatRenterMappingId = mappingId;
      this.applyPendingEntryById(mappingId);
    }
  }

  selectedBillingMonthValue(): string | null {
    if (!this.payment.paymentMonth || !this.payment.paymentYear) {
      return null;
    }
    return `${this.payment.paymentYear}-${this.payment.paymentMonth}`;
  }

  isBillingMonthLocked(): boolean {
    if (!this.isPrefillLocked) {
      return false;
    }
    return this.availableBillingMonths.length <= 1;
  }

  hasRemainingAdvance(): boolean {
    return this.availableAdvanceAmount > 0;
  }

  isAdjustFromAdvanceSelected(): boolean {
    return Number(this.payment.paymentMethod) === PaymentMethodEnum.ADJUST_FROM_ADVANCE;
  }

  onDateSelected(event: MatDatepickerInputEvent<Date>): void {
    this.successMessage = null;
    const pickedDate = event.value;
    if (!pickedDate || Number.isNaN(pickedDate.getTime())) {
      return;
    }

    const day = `${pickedDate.getDate()}`.padStart(2, '0');
    const month = `${pickedDate.getMonth() + 1}`.padStart(2, '0');
    const year = pickedDate.getFullYear();
    this.payment.paymentDate = `${day}-${month}-${year}`;
    this.matDateValue = new Date(year, pickedDate.getMonth(), pickedDate.getDate());
  }

  submitForm(): void {
    const payload = this.buildPayload();
    if (!payload) {
      return;
    }

    this.isSubmitting = true;
    this.error = null;
    this.successMessage = null;

    const request$ = this.mode === 'edit'
      ? this.payments.update(payload)
      : this.payments.create(payload);

    request$
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.save.emit({ mode: this.mode });
          if (this.mode === 'create') {
            this.successMessage = this.t('payment.successCreated', 'Payment created successfully.');
            this.openSuccessToast(this.successMessage);
            if (this.closeAfterSuccessfulAction) {
              this.close.emit();
              return;
            }
            this.resetAfterCreateSuccess();
          }
        },
        error: (err: any) => {
          const message = err?.message || `Failed to ${this.mode === 'edit' ? 'update' : 'create'} payment.`;
          this.error = message;
          this.openErrorToast(message);
        }
      });
  }

  canBuildPaymentSlip(): boolean {
    return Boolean(this.showBillFields && this.payment.flatRenterMappingId && this.payment.paymentMonth);
  }

  async downloadPaymentSlip(): Promise<void> {
    const data = this.buildPaymentSlipData();
    if (!data) {
      return;
    }

    try {
      await downloadPaymentSlipPdf(data);
      this.closeAfterSlipAction();
    } catch {
      this.error = this.t('payment.slipPrepareFailed', 'Failed to prepare payment slip.');
      this.openErrorToast(this.error);
    }
  }

  async printPaymentSlip(): Promise<void> {
    const data = this.buildPaymentSlipData();
    if (!data) {
      return;
    }

    try {
      await printPaymentSlipPdf(data);
      this.closeAfterSlipAction();
    } catch {
      this.error = this.t('payment.slipPrepareFailed', 'Failed to prepare payment slip.');
      this.openErrorToast(this.error);
    }
  }

  private closeAfterSlipAction(): void {
    if (this.closeAfterSuccessfulAction) {
      this.close.emit();
    }
  }

  private setDefaults(): void {
    const now = new Date();
    this.payment.paymentMonth = now.getMonth() + 1;
    const day = `${now.getDate()}`.padStart(2, '0');
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const year = now.getFullYear();
    this.payment.paymentDate = `${day}-${month}-${year}`;
    this.matDateValue = new Date(year, now.getMonth(), now.getDate());
  }

  private applyEditValues(): void {
    if (this.mode !== 'edit' || !this.initialPayment) {
      return;
    }

    const sourceFlat = this.initialPayment['flatRenterMapping']?.['flat'] as Record<string, unknown> | undefined;
    const sourceRenter = this.initialPayment['flatRenterMapping']?.['renter'] as Record<string, unknown> | undefined;
    const mappingId = Number(this.initialPayment['flatRenterMapping']?.['id'] ?? this.initialPayment['flatRenterMappingId']);
    const houseId = Number((sourceFlat?.['house'] as Record<string, unknown> | undefined)?.['id']);
    const flatId = Number(sourceFlat?.['id']);

    this.payment.houseId = Number.isFinite(houseId) ? houseId : null;
    this.payment.flatId = Number.isFinite(flatId) ? flatId : null;
    this.flatDropdownFilters = this.payment.houseId ? { 'house.id': this.payment.houseId } : {};

    this.payment.id = Number(this.initialPayment['id']) || null;
    this.payment.flatRenterMappingId = Number.isFinite(mappingId) ? mappingId : null;
    this.payment.paymentMonth = Number(this.initialPayment['paymentMonth']) || this.payment.paymentMonth;
    this.payment.paymentYear = `${this.initialPayment['paymentYear'] ?? this.payment.paymentYear}`;
    this.payment.paymentDate = this.toDisplayDateValue(this.initialPayment['paymentDate']) || this.payment.paymentDate;
    this.payment.rentAmount = Number(this.initialPayment['rentAmount'] ?? 0);
    this.payment.gasBillAmount = Number(this.initialPayment['gasBillAmount'] ?? 0);
    this.payment.electricBillAmount = Number(this.initialPayment['electricBillAmount'] ?? 0);
    this.payment.waterBillAmount = Number(this.initialPayment['waterBillAmount'] ?? 0);
    this.payment.serviceChargeAndOtherAmount = Number(this.initialPayment['serviceChargeAndOtherAmount'] ?? 0);
    this.payment.negativeChargeAmount = Number(this.initialPayment['negativeChargeAmount'] ?? 0);
    this.payment.advanceAdjustedAmount = Number(this.initialPayment['advanceAdjustedAmount'] ?? 0);
    this.payment.negativeChargeName = `${sourceFlat?.['negativeChargeName'] ?? ''}`.trim();
    this.payment.paymentMethod = Number(this.initialPayment['paymentMethod'] ?? PaymentMethodEnum.CASH);
    this.payment.remarks = `${this.initialPayment['remarks'] ?? ''}`;
    this.selectedRenterName = `${sourceRenter?.['renterName'] ?? ''}`;
    this.selectedRenterInfo = {
      mobile: `${sourceRenter?.['renterMobile'] ?? (sourceRenter?.['renterUser'] as Record<string, unknown> | undefined)?.['mobileNumber'] ?? ''}`,
      email: `${sourceRenter?.['renterEmail'] ?? ''}`,
      photoIdNo: `${sourceRenter?.['renterPhotoIdNo'] ?? ''}`,
      permanentAddress: `${sourceRenter?.['renterPermanentAddress'] ?? ''}`,
      previousAddress: `${sourceRenter?.['renterPreviousAddress'] ?? ''}`
    };
    this.showRenterInfo = false;
    this.currentAdvanceAmount = Number(this.initialPayment['advanceAmount'] ?? 0);
    this.availableAdvanceAmount = this.currentAdvanceAmount;

    this.matDateValue = this.toDateFromDisplay(this.payment.paymentDate);
    if (sourceFlat) {
      this.showBillFields = true;
      this.applyFlatBillRules(sourceFlat);
    }

    this.loadPendingByFlat();
  }

  private applyCreatePrefillValues(): void {
    if (this.mode !== 'create' || !this.prefillContext) {
      this.isPrefillLocked = false;
      return;
    }

    const houseId = Number(this.prefillContext.houseId);
    const flatId = Number(this.prefillContext.flatId);
    const mappingId = Number(this.prefillContext.pendingMappingId);
    const paymentMonth = Number(this.prefillContext.paymentMonth);
    const paymentYear = Number(this.prefillContext.paymentYear);

    this.payment.houseId = Number.isFinite(houseId) ? houseId : null;
    this.payment.flatId = Number.isFinite(flatId) ? flatId : null;
    this.payment.flatRenterMappingId = Number.isFinite(mappingId) ? mappingId : null;

    if (Number.isFinite(paymentMonth) && paymentMonth >= 1 && paymentMonth <= 12) {
      this.payment.paymentMonth = paymentMonth;
    }
    if (Number.isFinite(paymentYear) && paymentYear > 1900) {
      this.payment.paymentYear = `${paymentYear}`;
    }

    this.flatDropdownFilters = this.payment.houseId ? { 'house.id': this.payment.houseId } : {};
    this.isPrefillLocked = true;

    if (this.payment.flatId) {
      this.loadPendingByFlat();
    }
  }

  private loadPendingByFlat(): void {
    if (!this.payment.flatId) {
      return;
    }

    this.isLoadingPending = true;
    this.error = null;

    this.mappings.paymentPendingList({
      withTotals: true,
      filters: { 'flat.id': this.payment.flatId }
    }).pipe(finalize(() => (this.isLoadingPending = false)))
      .subscribe({
        next: (res: any) => {
          const data = Array.isArray(res?.data) ? res.data : [];
          this.pendingMappings = data;

          const selectedMappingId = this.payment.flatRenterMappingId && data.some((d: any) => Number(d?.id) === this.payment.flatRenterMappingId)
            ? this.payment.flatRenterMappingId
            : Number(data?.[0]?.id ?? NaN);

          if (Number.isFinite(selectedMappingId)) {
            this.payment.flatRenterMappingId = selectedMappingId;
            this.applyPendingEntryById(selectedMappingId);
          } else {
            this.resetPendingState();
            this.error = 'No pending billing found for selected flat.';
          }
        },
        error: (err: any) => {
          this.resetPendingState();
          this.error = err?.message || 'Failed to load pending billing info.';
        }
      });
  }

  private applyPendingEntryById(mappingId: number | null): void {
    const entry = this.pendingMappings.find((item) => Number(item?.['id']) === Number(mappingId));
    if (!entry) {
      return;
    }

    const renter = entry['renter'] as Record<string, unknown> | undefined;
    this.selectedRenterName = `${renter?.['renterName'] ?? ''}`;
    this.selectedRenterInfo = {
      mobile: `${renter?.['renterMobile'] ?? (renter?.['renterUser'] as Record<string, unknown> | undefined)?.['mobileNumber'] ?? ''}`,
      email: `${renter?.['renterEmail'] ?? ''}`,
      photoIdNo: `${renter?.['renterPhotoIdNo'] ?? ''}`,
      permanentAddress: `${renter?.['renterPermanentAddress'] ?? ''}`,
      previousAddress: `${renter?.['renterPreviousAddress'] ?? ''}`
    };
    this.showRenterInfo = false;
    const advanceAmount = Number(entry?.['advanceAmount'] ?? 0);
    const usedAdvanceAmount = Number(entry?.['usedAdvanceAmount'] ?? 0);
    this.currentAdvanceAmount = Math.max(0, advanceAmount);
    this.availableAdvanceAmount = Math.max(0, advanceAmount - usedAdvanceAmount);
    this.normalizePaymentMethodForAdvance();
    this.showBillFields = true;
    const entryId = Number(entry['id']);
    this.buildBillingMonthOptions(entry['billingMonths'] as Record<string, unknown> | undefined, entryId);
    this.applyFlatBillRules(entry['flat'] as Record<string, unknown> | undefined);
  }

  private buildBillingMonthOptions(
    billingMonths: Record<string, unknown> | undefined,
    mappingId: number
  ): void {
    const options: Array<{ value: string; label: string; mappingId: number }> = [];
    const years = Object.keys(billingMonths ?? {}).sort((a, b) => Number(b) - Number(a));

    years.forEach((year) => {
      const monthsRaw = (billingMonths?.[year] as unknown[]) ?? [];
      const months = monthsRaw
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item >= 1 && item <= 12)
        .sort((a, b) => b - a);

      months.forEach((monthNo) => {
        const monthLabel = new Date(2000, monthNo - 1, 1).toLocaleString('en-US', { month: 'long' });
        options.push({
          value: `${year}-${monthNo}`,
          label: `${monthLabel}, ${year}`,
          mappingId
        });
      });
    });

    // In edit mode, include the current payment month even if backend pending list does not return it.
    // This keeps the previously paid month/year visible and selected.
    if (this.mode === 'edit' && this.payment.paymentMonth && this.payment.paymentYear) {
      const currentValue = `${this.payment.paymentYear}-${this.payment.paymentMonth}`;
      const alreadyExists = options.some((item) => item.value === currentValue);
      if (!alreadyExists) {
        const monthNo = Number(this.payment.paymentMonth);
        const monthLabel = monthNo >= 1 && monthNo <= 12
          ? new Date(2000, monthNo - 1, 1).toLocaleString('en-US', { month: 'long' })
          : 'Unknown';
        options.unshift({
          value: currentValue,
          label: `${monthLabel}, ${this.payment.paymentYear}`,
          mappingId
        });
      }
    }

    this.availableBillingMonths = options;
    this.billingMonthDropdownObj = {
      ...this.billingMonthDropdownObj,
      options
    };

    const hasCurrentSelection = options.some(
      (item) => item.value === `${this.payment.paymentYear}-${this.payment.paymentMonth}`
    );

    if (!hasCurrentSelection) {
      if (options.length > 0) {
        const first = options[0].value;
        const [yearText, monthText] = first.split('-');
        this.payment.paymentYear = `${yearText}`;
        this.payment.paymentMonth = Number(monthText);
      } else {
        this.payment.paymentMonth = null;
      }
    }
  }

  private applyFlatBillRules(flat: Record<string, unknown> | undefined): void {
    this.billRequired = {
      gas: Boolean(flat?.['gasBillRequired']),
      electric: Boolean(flat?.['electricBillRequired']),
      water: Boolean(flat?.['waterBillRequired']),
      service: Boolean(flat?.['serviceChargeAndOtherRequired']),
      other: Boolean(flat?.['negativeChargeRequired'])
    };

    if (this.mode === 'create') {
      this.payment.rentAmount = Number(flat?.['rentAmount'] ?? 0);
      this.payment.gasBillAmount = this.billRequired.gas ? Number(flat?.['gasBillAmount'] ?? 0) : 0;
      this.payment.electricBillAmount = this.billRequired.electric ? Number(flat?.['electricBillAmount'] ?? 0) : 0;
      this.payment.waterBillAmount = this.billRequired.water ? Number(flat?.['waterBillAmount'] ?? 0) : 0;
      this.payment.serviceChargeAndOtherAmount = this.billRequired.service ? Number(flat?.['serviceChargeAndOtherAmount'] ?? 0) : 0;
      this.payment.negativeChargeAmount = this.billRequired.other ? Number(flat?.['negativeChargeAmount'] ?? 0) : 0;
      this.payment.negativeChargeName = this.billRequired.other ? `${flat?.['negativeChargeName'] ?? ''}` : '';
    } else {
      if (!this.billRequired.gas) this.payment.gasBillAmount = 0;
      if (!this.billRequired.electric) this.payment.electricBillAmount = 0;
      if (!this.billRequired.water) this.payment.waterBillAmount = 0;
      if (!this.billRequired.service) this.payment.serviceChargeAndOtherAmount = 0;
      if (!this.billRequired.other) {
        this.payment.negativeChargeAmount = 0;
        this.payment.negativeChargeName = '';
      }
    }
  }

  private resetPendingState(): void {
    this.pendingMappings = [];
    this.availableBillingMonths = [];
    this.billingMonthDropdownObj = {
      ...this.billingMonthDropdownObj,
      options: []
    };
    this.selectedRenterName = '';
    this.selectedRenterInfo = {
      mobile: '',
      email: '',
      photoIdNo: '',
      permanentAddress: '',
      previousAddress: ''
    };
    this.showRenterInfo = false;
    this.showBillFields = false;
    this.payment.paymentMonth = null;
    this.payment.paymentMethod = PaymentMethodEnum.CASH;
    this.currentAdvanceAmount = 0;
    this.availableAdvanceAmount = 0;
  }

  private normalizePaymentMethodForAdvance(): void {
    if (!this.hasRemainingAdvance() && this.isAdjustFromAdvanceSelected()) {
      this.payment.paymentMethod = PaymentMethodEnum.CASH;
      this.payment.advanceAdjustedAmount = 0;
    }
  }

  private buildPayload(): Record<string, any> | null {
    if (!this.payment.flatRenterMappingId) {
      this.error = 'Renter mapping is required.';
      return null;
    }

    if (!this.payment.paymentDate) {
      this.error = 'Payment date is required.';
      return null;
    }

    if (!this.payment.paymentMonth) {
      this.error = 'Billing month is required.';
      return null;
    }

    const paymentDate = this.toApiDateValue(this.payment.paymentDate);
    if (!paymentDate) {
      this.error = 'Payment date must be valid.';
      return null;
    }

    if (!this.hasRemainingAdvance() || !this.isAdjustFromAdvanceSelected()) {
      this.payment.advanceAdjustedAmount = 0;
    }

    if (this.payment.advanceAdjustedAmount < 0) {
      this.error = 'Advance adjusted amount cannot be negative.';
      return null;
    }

    if (this.hasRemainingAdvance() && this.payment.advanceAdjustedAmount > this.availableAdvanceAmount) {
      this.error = `Advance adjusted amount cannot exceed remaining advance (৳${this.availableAdvanceAmount.toLocaleString('en-US')}).`;
      return null;
    }

    const payload: Record<string, any> = {
      flatRenterMapping: { id: this.payment.flatRenterMappingId },
      paymentDate,
      paymentMonth: this.payment.paymentMonth,
      paymentYear: this.payment.paymentYear,
      rentAmount: Number(this.payment.rentAmount || 0),
      gasBillAmount: this.billRequired.gas ? Number(this.payment.gasBillAmount || 0) : 0,
      electricBillAmount: this.billRequired.electric ? Number(this.payment.electricBillAmount || 0) : 0,
      waterBillAmount: this.billRequired.water ? Number(this.payment.waterBillAmount || 0) : 0,
      serviceChargeAndOtherAmount: this.billRequired.service ? Number(this.payment.serviceChargeAndOtherAmount || 0) : 0,
      negativeChargeAmount: this.billRequired.other ? Number(this.payment.negativeChargeAmount || 0) : 0,
      advanceAdjustedAmount: (this.hasRemainingAdvance() && this.isAdjustFromAdvanceSelected())
        ? Number(this.payment.advanceAdjustedAmount || 0)
        : 0,
      paymentMethod: Number(this.payment.paymentMethod || PaymentMethodEnum.CASH),
      remarks: `${this.payment.remarks ?? ''}`.trim(),
      status: 1
    };

    if (this.mode === 'edit') {
      if (!this.payment.id) {
        this.error = 'Payment id is missing for update.';
        return null;
      }
      payload['id'] = this.payment.id;
    }

    return payload;
  }

  private buildPaymentSlipData(): PaymentReceiptData | null {
    if (!this.canBuildPaymentSlip()) {
      this.error = this.t('payment.slipMissingData', 'Select flat and billing month first.');
      this.openErrorToast(this.error);
      return null;
    }

    const currentEntry = this.currentPendingEntry();
    const initialMapping = this.initialPayment?.['flatRenterMapping'] as Record<string, unknown> | undefined;
    const flat = (currentEntry?.['flat'] as Record<string, unknown> | undefined)
      ?? (initialMapping?.['flat'] as Record<string, unknown> | undefined);
    const renter = (currentEntry?.['renter'] as Record<string, unknown> | undefined)
      ?? (initialMapping?.['renter'] as Record<string, unknown> | undefined);
    const house = flat?.['house'] as Record<string, unknown> | undefined;
    const paymentDate = this.payment.paymentDate || this.displayDateFromDate(new Date());
    const createdOn = this.formatDateTime(new Date().toISOString());
    const monthLabel = this.paymentMonthLabel(this.payment.paymentMonth, this.payment.paymentYear);
    const negativeChargeLabel = `${this.payment.negativeChargeName || flat?.['negativeChargeName'] || ''}`.trim() || 'Negative Charge';
    const amountRows = ([
      ['Rent', Number(this.payment.rentAmount ?? 0)] as [string, number],
      ['Gas', Number(this.payment.gasBillAmount ?? 0)] as [string, number],
      ['Electric', Number(this.payment.electricBillAmount ?? 0)] as [string, number],
      ['Water', Number(this.payment.waterBillAmount ?? 0)] as [string, number],
      ['Service Charge + Other', Number(this.payment.serviceChargeAndOtherAmount ?? 0)] as [string, number],
      [negativeChargeLabel, Number(this.payment.negativeChargeAmount ?? 0) * -1] as [string, number],
      ['Adjust From Advance', Number(this.payment.advanceAdjustedAmount ?? 0) * -1] as [string, number]
    ]).filter(([, amount]) => amount !== 0);

    const total = amountRows.reduce((sum, [, amount]) => sum + Number(amount ?? 0), 0);
    return {
      txn: `${this.initialPayment?.['txnNo'] ?? this.payment.id ?? 'N/A'}`,
      monthLabel,
      paymentDate,
      createdOn,
      paymentMethod: this.paymentMethodLabel(this.payment.paymentMethod),
      status: 'UNPAID',
      organizationName: this.receiptOrganizationName,
      flatName: `${flat?.['flatName'] ?? 'N/A'}`,
      floorName: `${flat?.['floorName'] ?? 'N/A'}`,
      houseName: `${house?.['houseName'] ?? 'N/A'}`,
      renterName: `${(renter?.['renterName'] ?? this.selectedRenterName) || 'N/A'}`,
      amountRows,
      total,
      brandName: this.receiptBrandName,
      brandLogoUrl: this.receiptBrandLogoUrl,
      generatedOn: createdOn,
      generatedBy: this.resolveCurrentUserLabel(),
      developedBy: APP_DEVELOPED_BY
    };
  }

  private currentPendingEntry(): Record<string, unknown> | null {
    const mappingId = Number(this.payment.flatRenterMappingId);
    if (!Number.isFinite(mappingId)) {
      return null;
    }
    return (this.pendingMappings.find((item) => Number(item?.['id']) === mappingId) as Record<string, unknown> | undefined) ?? null;
  }

  private paymentMonthLabel(month: number | null | undefined, year: number | string | null | undefined): string {
    const monthNo = Number(month);
    if (!Number.isFinite(monthNo) || monthNo < 1 || monthNo > 12) {
      return 'N/A';
    }
    const monthName = new Date(2000, monthNo - 1, 1).toLocaleString('en-US', { month: 'long' });
    return `${monthName} ${year ?? ''}`.trim();
  }

  private paymentMethodLabel(value: number | string | null | undefined): string {
    const method = Number(value);
    switch (method) {
      case PaymentMethodEnum.CASH:
        return 'Cash';
      case PaymentMethodEnum.BANK:
        return 'Bank';
      case PaymentMethodEnum.CHEQUE:
        return 'Cheque';
      case PaymentMethodEnum.MOBILE_BANKING:
        return 'Mobile Banking';
      case PaymentMethodEnum.ADJUST_FROM_ADVANCE:
        return 'Adjust From Advance';
      case PaymentMethodEnum.OTHER:
        return 'Other';
      default:
        return 'N/A';
    }
  }

  private formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    const rawHours = date.getHours();
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = rawHours >= 12 ? 'pm' : 'am';
    const hours12 = rawHours % 12 || 12;
    return `${day}-${month}-${year} ${`${hours12}`.padStart(2, '0')}:${minutes} ${period}`;
  }

  private displayDateFromDate(date: Date): string {
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  private resolveCurrentUserLabel(): string {
    const token = `${this.tokens.accessToken ?? ''}`.trim();
    if (!token) {
      return 'N/A';
    }

    const parts = token.split('.');
    if (parts.length < 2) {
      return 'N/A';
    }

    try {
      const base64 = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(parts[1].length / 4) * 4, '=');
      const jsonText = atob(base64);
      const payload = JSON.parse(jsonText) as Record<string, unknown>;
      const label = `${payload?.['name'] ?? payload?.['username'] ?? payload?.['preferred_username'] ?? payload?.['sub'] ?? ''}`.trim();
      return label || 'N/A';
    } catch {
      return 'N/A';
    }
  }

  private resetAfterCreateSuccess(): void {
    const now = new Date();
    this.payment.paymentDate = `${`${now.getDate()}`.padStart(2, '0')}-${`${now.getMonth() + 1}`.padStart(2, '0')}-${now.getFullYear()}`;
    this.matDateValue = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    this.payment.paymentYear = `${now.getFullYear()}`;
    this.payment.flatId = null;
    this.payment.flatRenterMappingId = null;
    this.payment.paymentMonth = null;
    this.payment.rentAmount = 0;
    this.payment.gasBillAmount = 0;
    this.payment.electricBillAmount = 0;
    this.payment.waterBillAmount = 0;
    this.payment.serviceChargeAndOtherAmount = 0;
    this.payment.negativeChargeAmount = 0;
    this.payment.advanceAdjustedAmount = 0;
    this.payment.negativeChargeName = '';
    this.payment.paymentMethod = PaymentMethodEnum.CASH;
    this.payment.remarks = '';
    this.resetPendingState();
  }

  private toDisplayDateValue(value: unknown): string {
    if (!value) {
      return '';
    }

    const text = `${value}`;
    if (/^\d{2}-\d{2}-\d{4}$/.test(text)) {
      return text;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      const [year, month, day] = text.split('-');
      return `${day}-${month}-${year}`;
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const day = `${parsed.getDate()}`.padStart(2, '0');
    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }

  private toApiDateValue(value: string): string | null {
    const trimmed = `${value ?? ''}`.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const match = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!match) {
      return null;
    }
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    return `${year}-${`${month}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`;
  }

  private toDateFromDisplay(value: string): Date | null {
    const match = (value ?? '').trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!match) {
      return null;
    }
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    return parsed;
  }

  private openSuccessToast(message: string): void {
    this.snackBar.open(message, this.t('common.close', 'Close'), {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    });
  }

  private openErrorToast(message: string): void {
    this.snackBar.open(message, this.t('common.close', 'Close'), {
      duration: 3500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['dashboard-toast-warning']
    });
  }

  private t(key: string, fallback: string): string {
    return this.language.translate(key, fallback);
  }
}

